import { RequestParams } from '@bv/api';
import { WithPagination } from '@bv/api/crud';
import { useToast } from '@bv/composables';

import { useWorkflowStore } from '../../store/workflow';
import { contactFieldsOptions } from '../contact-fields-options';

import { contactIdentifierTypeOptions, generateWebhookCredentials, resolveWebhookUrl } from './incoming-webhook';

import type { StepConfig, FieldValues, SelectField, TextField } from '../../types/workflow';

interface TriggerConditionParentValues extends FieldValues {
  attributeKey?: string;
  triggerType?: string;
  operator?: string;
  values?: string[];
  contactField?: string;
}

// Create channels API instance
class ChannelsApi extends WithPagination {
  constructor() {
    super('conversations/channels');
  }
}

const channelsApi = new ChannelsApi();

export const channelsOptions = async (config?: { params?: RequestParams }) => {
  const { data, meta } = await channelsApi.paginated(config);
  return {
    meta,
    data: (data as Array<{ name: string; _id: string }>).map((channel) => ({
      label: channel.name,
      value: channel._id,
    })),
  };
};

// Create closing notes API instance for Conversation Closed trigger conditions
class ClosingNotesApi extends WithPagination {
  constructor() {
    super('conversations/closing-notes');
  }
}

const closingNotesApi = new ClosingNotesApi();

export const closingNotesOptions = async (config?: { params?: RequestParams }) => {
  const { data, meta } = await closingNotesApi.paginated(config);
  return {
    meta,
    data: (data as Array<{ category: string; _id: string }>).map((note) => ({
      label: note.category,
      value: note._id,
    })),
  };
};

// Create tags API instance
class TagsApi extends WithPagination {
  constructor() {
    super('contacts/tags');
  }
}

const tagsApi = new TagsApi();

/** Paginated contact tags for branch conditions and other selects */
export const contactTagsOptions = async (config?: { params?: RequestParams }) => {
  const { data, meta } = await tagsApi.paginated(config);
  return {
    meta,
    data: (data as Array<{ name: string; id: string }>).map((tag) => ({
      label: tag.name,
      value: tag.name,
    })),
  };
};

const triggerOptions = {
  ConversationOpened: 'Conversation Opened',
  ConversationClosed: 'Conversation Closed',
  Shortcut: 'Shortcut',
  CsatSubmitted: 'CSAT rating submitted',
  InvokedInternally: 'Internal Workflow',
  ContactFieldUpdated: 'Contact Field Updated',
  ContactTagUpdated: 'Contact Tag Updated',
  Webhook: 'Incoming Webhook',
};

export const triggerTypeLabels = triggerOptions;

const isWebhookTrigger = (triggerType?: string) => triggerType === 'Webhook';

const setWebhookFieldVisibility = (
  triggerType: string,
  allFields: Record<string, { show?: boolean | ((values: FieldValues) => boolean) }>,
) => {
  const showWebhookFields = isWebhookTrigger(triggerType);

  allFields.webhookUrl.show = showWebhookFields;
  allFields.regenerateWebhookUrl.show = showWebhookFields;
  allFields.channelId.show = showWebhookFields;
  allFields.contactIdentifierSection.show = showWebhookFields;
  allFields.variableConfigurationSection.show = showWebhookFields;
};

const requestWebhookCredentials = (allValues: FieldValues, regenerate = false) => {
  const existingToken = typeof allValues.webhookToken === 'string' ? allValues.webhookToken : undefined;

  if (!regenerate && existingToken) {
    const webhookUrl = resolveWebhookUrl(allValues as Record<string, unknown>);
    allValues.webhookUrl = webhookUrl;

    const store = useWorkflowStore();
    if (store.selectedNode?.category === 'Trigger' && !store.selectedNode.data.webhookUrl && webhookUrl) {
      store.updateNodeData(store.selectedNode.id, {
        webhookUrl,
      });
    }
    return;
  }

  generateWebhookCredentials(regenerate ? existingToken : undefined)
    .then(({ webhookToken, webhookUrl }) => {
      const store = useWorkflowStore();
      const selectedNode = store.selectedNode;

      if (!selectedNode || selectedNode.category !== 'Trigger') {
        return;
      }

      store.updateNodeData(selectedNode.id, {
        webhookToken,
        webhookUrl,
      });
    })
    .catch(() => {
      const { error } = useToast();
      error('Failed to generate webhook URL. Please try again.');
    });
};

const trigger: StepConfig = {
  // Design
  icon: 'trigger',
  headerTextColor: '#E96031',
  headerColor: '#FBDFD6',
  title: 'Trigger',
  text: 'Conversation Opened',
  category: 'Trigger',
  desc: 'Choose the Trigger that will start this Workflow for your conversations.',
  primaryKey: (parentValues) => {
    return triggerOptions[parentValues.triggerType as keyof typeof triggerOptions];
  },
  variableFields: [
    {
      fieldKey: 'variables',
      variableProperty: 'name',
      isArray: true,
    },
  ],

  // Form
  formFields: [
    {
      key: 'triggerType',
      label: 'When',
      type: 'select',
      defaultValue: 'ConversationOpened',
      options: () => {
        return Object.keys(triggerOptions).map((key) => ({
          label: triggerOptions[key as keyof typeof triggerOptions],
          value: key,
        }));
      },
      onChange: (value, allValues, allFields) => {
        if (!allFields?.triggerConditions) {
          return;
        }
        // when change i need to reset all the data of the triggerConditions
        if (
          value == 'Shortcut' ||
          value == 'CsatSubmitted' ||
          value == 'ContactFieldUpdated' ||
          value == 'ContactTagUpdated' ||
          value == 'InvokedInternally' ||
          value == 'Webhook'
        ) {
          allFields.triggerConditions.show = false;
        } else {
          allFields.triggerConditions.show = true;
        }
        if (value == 'ContactFieldUpdated') {
          allFields.field.show = true;
        } else {
          allFields.field.show = false;
        }

        if (value == 'ContactTagUpdated') {
          allFields['operation'].show = true;
          allFields['tags'].show = true;
        } else {
          allFields['operation'].show = false;
          allFields['tags'].show = false;
        }

        setWebhookFieldVisibility(String(value), allFields);

        if (value === 'Webhook') {
          requestWebhookCredentials(allValues);
          const store = useWorkflowStore();
          if (store.selectedNode?.category === 'Trigger') {
            store.updateNodeName(store.selectedNode.id, 'Incoming Webhook');
          }
        }

        // Update the triggerType in the form data
        if (allValues) {
          allValues.triggerType = value;
        }
      },
    },
    {
      key: 'webhookUrl',
      label: 'Webhook URL',
      type: 'text',
      defaultValue: '',
      disabled: true,
      copyable: true,
      dependsOn: ['triggerType'],
      desc: 'Triggers for a contact when an external app or service sends a HTTP POST request to this URL.',
      show: (parentValues) => isWebhookTrigger(parentValues.triggerType as string),
    },
    {
      key: 'regenerateWebhookUrl',
      label: 'Regenerate URL',
      type: 'button',
      buttonText: 'Regenerate URL',
      buttonType: 'secondary',
      dependsOn: ['triggerType'],
      show: (parentValues) => isWebhookTrigger(parentValues.triggerType as string),
      onClick: (_values, _fields, params?: Record<string, unknown>) => {
        const store = params?.store as ReturnType<typeof useWorkflowStore> | undefined;
        const selectedNode = store?.selectedNode;

        if (!selectedNode || !store) {
          return;
        }

        const existingToken =
          typeof selectedNode.data?.webhookToken === 'string' ? selectedNode.data.webhookToken : undefined;

        generateWebhookCredentials(existingToken)
          .then(({ webhookToken, webhookUrl }) => {
            store.updateNodeData(selectedNode.id, {
              webhookToken,
              webhookUrl,
            });
          })
          .catch(() => {
            const { error } = useToast();
            error('Failed to regenerate webhook URL. Please try again.');
          });
      },
    },
    {
      key: 'channelId',
      label: 'Choose channel',
      type: 'select',
      placeholder: 'Select channel',
      searchKey: 'name',
      defaultValue: '',
      dependsOn: ['triggerType'],
      show: (parentValues) => isWebhookTrigger(parentValues.triggerType as string),
      options: () => channelsOptions,
      validation: {
        required: true,
      },
    },
    {
      key: 'contactIdentifierSection',
      label: 'Contact Identifier',
      type: 'accordion',
      defaultValue: true,
      dependsOn: ['triggerType'],
      show: (parentValues) => isWebhookTrigger(parentValues.triggerType as string),
      fields: [
        {
          key: 'contactIdentifierKey',
          label: 'JSON Key',
          type: 'text',
          placeholder: 'JSON Key',
          defaultValue: '',
          validation: {
            required: true,
          },
        },
        {
          key: 'contactIdentifierType',
          label: 'Identifier Type',
          type: 'select',
          defaultValue: 'phone_number',
          options: contactIdentifierTypeOptions,
          validation: {
            required: true,
          },
        },
      ],
    },
    {
      key: 'variableConfigurationSection',
      label: 'Variable Configuration',
      type: 'accordion',
      defaultValue: true,
      dependsOn: ['triggerType'],
      desc: 'Store payload data items as variables for use in later steps.',
      show: (parentValues) => isWebhookTrigger(parentValues.triggerType as string),
      fields: [
        {
          key: 'variables',
          type: 'formRepeater',
          defaultValue: [],
          config: {
            maxItems: 20,
            minItems: 0,
            addButtonText: 'Add New',
            showLogicalOperator: false,
            fields: [
              {
                key: 'key',
                type: 'text',
                label: 'JSON Key',
                placeholder: 'JSON Key',
                defaultValue: '',
                validation: {
                  required: true,
                },
              },
              {
                key: 'name',
                type: 'text',
                label: 'Variable Name',
                placeholder: 'Variable Name',
                defaultValue: '',
                validation: {
                  required: true,
                  textNoSpecialChars: true,
                },
              },
            ],
          },
        },
      ],
    },
    {
      key: 'triggerConditions',
      label: 'Trigger Conditions',
      type: 'accordion',
      defaultValue: false,
      show: (parentValues: TriggerConditionParentValues) => {
        // hide trigger conditions for Shortcut and CsatSubmitted
        return (
          parentValues.triggerType !== 'Shortcut' &&
          parentValues.triggerType !== 'CsatSubmitted' &&
          parentValues.triggerType !== 'ContactFieldUpdated' &&
          parentValues.triggerType !== 'ContactTagUpdated' &&
          parentValues.triggerType !== 'InvokedInternally' &&
          parentValues.triggerType !== 'Webhook'
        );
      },
      dependsOn: ['triggerType'],
      fields: [
        {
          key: 'conditions',
          type: 'formRepeater',
          defaultValue: [],
          config: {
            maxItems: 10,
            minItems: 0,
            itemLabel: 'Condition',
            addButtonText: 'Add Condition',
            showLogicalOperator: true,
            fields: [
              {
                key: 'attributeKey',
                type: 'select',
                defaultValue: 'source',
                dependsOn: ['triggerType'],
                options: (parentValues: TriggerConditionParentValues, allValues: any) => {
                  const base = [
                    { label: 'Source', value: 'source' },
                    { label: 'Channel', value: 'channel' },
                  ];
                  if (allValues?.triggerType === 'ConversationOpened') {
                    base.push({ label: 'Last Incoming Message', value: 'lastIncomingMessage' });
                  }
                  if (allValues?.triggerType === 'ConversationClosed') {
                    base.push({ label: 'Category', value: 'category' });
                  }
                  return base;
                },
                onChange(value, allValues, allFields) {
                  if (value === 'source') {
                    allFields.values.type = 'select';
                    delete (allFields.values as SelectField).validation?.maxLength;
                  } else if (value === 'channel') {
                    allFields.values.type = 'multiSelect';
                    delete (allFields.values as SelectField).validation?.maxLength;
                  } else if (value === 'lastIncomingMessage') {
                    allFields.values.type = 'text';
                    if (!(allFields.values as TextField).validation) {
                      (allFields.values as TextField).validation = {};
                    }
                    const validation = (allFields.values as TextField).validation;
                    if (validation) {
                      validation.maxLength = 500;
                    }
                  } else if (value === 'category') {
                    const op = allValues.operator;
                    if (op === 'equalTo' || op === 'notEqualTo' || op === 'exists' || op === 'notExists') {
                      allFields.values.type = 'select';
                    } else {
                      allFields.values.type = 'multiSelect';
                    }
                    delete (allFields.values as SelectField).validation?.maxLength;
                  } else {
                    allFields.values.type = 'multiSelect';
                    delete (allFields.values as SelectField).validation?.maxLength;
                  }
                },
              },
              {
                key: 'operator',
                type: 'select',
                defaultValue: 'equalTo',
                dependsOn: ['attributeKey'],
                options: (parentValues: TriggerConditionParentValues) => {
                  if (parentValues.attributeKey === 'lastIncomingMessage') {
                    return [
                      { label: 'Equal to', value: 'equalTo' },
                      { label: 'Not equal to', value: 'notEqualTo' },
                      { label: 'Contains', value: 'contains' },
                      { label: 'Does not contain', value: 'notContains' },
                    ];
                  }
                  if (parentValues.attributeKey === 'category') {
                    return [
                      { label: 'Equal to', value: 'equalTo' },
                      { label: 'Not equal to', value: 'notEqualTo' },
                      { label: 'Exists', value: 'exists' },
                      { label: 'Not exists', value: 'notExists' },
                      { label: 'Has Any Of', value: 'hasAnyOf' },
                      { label: 'Has None Of', value: 'hasNoneOf' },
                      { label: 'Has All Of', value: 'hasAllOf' },
                    ];
                  }
                  return [
                    { label: 'Equal to', value: 'equalTo' },
                    { label: 'Not equal to', value: 'notEqualTo' },
                  ];
                },
                onChange: (value, allValues, allFields) => {
                  if (allValues.attributeKey === 'category' && allFields?.values) {
                    if (value === 'equalTo' || value === 'notEqualTo' || value === 'exists' || value === 'notExists') {
                      allFields.values.type = 'select';
                    } else if (value === 'hasAnyOf' || value === 'hasNoneOf' || value === 'hasAllOf') {
                      allFields.values.type = 'multiSelect';
                    }
                  }
                },
              },
              {
                key: 'values',
                type: (parentValues: TriggerConditionParentValues) => {
                  if (parentValues.attributeKey === 'source') {
                    return 'select';
                  }
                  if (parentValues.attributeKey === 'channel') {
                    return 'multiSelect';
                  }
                  if (parentValues.attributeKey === 'lastIncomingMessage') {
                    return 'text';
                  }
                  if (parentValues.attributeKey === 'category') {
                    if (parentValues.operator === 'equalTo' || parentValues.operator === 'notEqualTo') {
                      return 'select';
                    }
                    if (
                      parentValues.operator === 'hasAnyOf' ||
                      parentValues.operator === 'hasNoneOf' ||
                      parentValues.operator === 'hasAllOf'
                    ) {
                      return 'multiSelect';
                    }
                    return 'select';
                  }
                  return 'multiSelect';
                },
                searchKey: 'name',
                defaultValue: [],
                dependsOn: ['attributeKey', 'triggerType', 'operator'],
                show: (parentValues: TriggerConditionParentValues) => {
                  if (parentValues.attributeKey === 'category') {
                    return parentValues.operator !== 'exists' && parentValues.operator !== 'notExists';
                  }
                  return true;
                },
                validation: {
                  required: true,
                  minSelections: 1,
                },
                options: (parentValues: TriggerConditionParentValues, allValues: any) => {
                  if (parentValues.attributeKey === 'source') {
                    // Base options for all trigger types
                    const sourceOptions = [
                      { label: 'User', value: 'user' },
                      { label: 'Workflow', value: 'workflow' },
                    ];

                    // Only include 'Contact' option for ConversationOpened and Shortcut trigger types
                    if (allValues.triggerType !== 'ConversationClosed') {
                      sourceOptions.push({ label: 'Contact', value: 'contact' });
                      sourceOptions.push({ label: 'Click to chat Ads', value: 'Ads' });
                      sourceOptions.push({ label: 'Broadcast', value: 'MessageAfterBroadcast' });
                    }

                    return sourceOptions;
                  } else if (parentValues.attributeKey === 'channel') {
                    return channelsOptions;
                  } else if (parentValues.attributeKey === 'category') {
                    return closingNotesOptions;
                  }

                  return [];
                },
              },
            ],
          },
        },
      ],
    },
    {
      key: 'field',
      label: 'Field',
      type: 'select',
      searchKey: 'filter[q]',
      defaultValue: '',
      options: contactFieldsOptions,
      show: (parentValues) => {
        return parentValues.triggerType === 'ContactFieldUpdated';
      },
      dependsOn: ['triggerType'],
      validation: {
        required: true,
      },
    },
    {
      key: 'operation',
      label: 'Action',
      type: 'select',
      defaultValue: 'added',
      options: [
        { label: 'Tag is added', value: 'added' },
        { label: 'Tag is removed', value: 'removed' },
      ],
      dependsOn: ['triggerType'],
      show: (parentValues) => {
        return parentValues.triggerType === 'ContactTagUpdated';
      },
      validation: {
        required: true,
      },
    },
    {
      key: 'tags',
      label: 'Tags',
      type: 'multiSelect',
      searchKey: 'filter[q]',
      defaultValue: [],
      options: () => {
        // Return a function that will be called by FormField to fetch tags using the API instance
        return async (config?: { params?: RequestParams }) => {
          return contactTagsOptions(config);
        };
      },
      dependsOn: ['triggerType'],
      show: (parentValues) => {
        return parentValues.triggerType === 'ContactTagUpdated';
      },
      validation: {
        required: true,
        minSelections: 1,
      },
    },
    {
      key: 'advancedSettings',
      label: 'Advanced Settings',
      type: 'accordion',
      defaultValue: true,
      show: true,
      style: {
        marginTop: 'auto',
      },
      fields: [
        {
          key: 'triggerOncePerConversation',
          type: 'toggle',
          label: 'Trigger once per contact',
          defaultValue: false,
          validation: {
            required: false,
          },
        },
      ],
    },
  ],
};

export default trigger;
